import { Box, SxProps, Theme } from "@mui/material";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import React, { FC, useEffect, useState } from "react";
import { IListSearchPayload } from "../List/list.types";
import { Environment } from "../../Config/constants";
import { TABLE_STICKY_MAX_HEIGHT } from "../../Styles/table-layout";

interface ITableProps {
  columns: GridColDef[];
  data: any[];
  totalCount: number;
  onSelect?: (selectedIds: any[]) => void;
  onRowClick?: (row: any) => void;
  isRowSelectable?: (params: any) => boolean;
  onPageChange?: (data: IListSearchPayload) => void;
  onSortModelChange?: (sortModel: GridSortModel) => void;
  search?: string;
  sx?: SxProps<Theme>;
  disableRowSelectionOnClick?: boolean;
  pagination?: boolean;
  checkboxSelection?: boolean;
  sortingMode?: 'client' | 'server';
  rowHeight?: number;
  stickyHeader?: boolean;
  maxHeight?: string | number;
  sortModel?: GridSortModel;
  page?: number;
}

const dataGridSx = {
  border: 'none',
  bgcolor: 'background.paper',
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: 'background.paper',
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-row': {
    bgcolor: 'background.paper',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  '& .MuiDataGrid-virtualScroller': {
    minHeight: 'unset !important',
    bgcolor: 'background.paper',
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    minHeight: 'unset !important',
    bgcolor: 'background.paper',
  },
  '& .MuiDataGrid-main': {
    overflow: 'visible',
    bgcolor: 'background.paper',
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderColor: 'divider',
  },
} as const;

const Table: FC<ITableProps> = ({
  columns,
  data,
  totalCount,
  onSelect,
  onRowClick,
  isRowSelectable,
  onPageChange,
  onSortModelChange,
  search,
  disableRowSelectionOnClick = true,
  pagination = true,
  checkboxSelection = false,
  sortingMode = 'server',
  rowHeight = 120,
  stickyHeader = false,
  maxHeight = TABLE_STICKY_MAX_HEIGHT,
  sortModel,
  page: externalPage,
  sx: containerSx,
  ...rest
}) => {
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (externalPage !== undefined) {
      setPage(Math.max(0, externalPage - 1));
    }
  }, [externalPage]);

  const paginationPage = externalPage !== undefined ? Math.max(0, externalPage - 1) : page;

  const handleRowSelection = (selectionModel: any) => {
    if (onSelect) {
      onSelect(selectionModel);
    }
  };

  const handleRowClick = (params: any) => {
    if (onRowClick) {
      onRowClick(params.row);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage !== paginationPage) {
      setPage(newPage);
      if (onPageChange) {
        onPageChange({ page: newPage + 1, searchBy: search });
      }
    }
  };

  const handleSortModelChange = (nextSortModel: GridSortModel) => {
    if (onSortModelChange) {
      onSortModelChange(nextSortModel);
    }
  };

  const isCompact = rowHeight <= 64;
  const isDense = rowHeight <= 48;
  const columnHeaderHeight = isDense ? 40 : isCompact ? 44 : 56;
  const useAutoHeight = !pagination;
  const useFixedViewportHeight = pagination && stickyHeader;

  const updatedColumns = columns.map((col) => ({
    ...col,
    sortable: col.sortable !== undefined ? col.sortable : false,
    disableColumnMenu: true,
  }));

  const gridSx = {
    ...dataGridSx,
    height: useFixedViewportHeight ? '100%' : 'auto',
    minHeight: 'unset',
    '& .MuiDataGrid-columnHeader': isCompact
      ? { padding: isDense ? '0 12px' : '4px 12px' }
      : undefined,
    '& .MuiDataGrid-columnHeaderTitle': isCompact
      ? { fontSize: isDense ? '0.8125rem' : '0.875rem', fontWeight: 600 }
      : undefined,
    '& .MuiDataGrid-cell': {
      border: 'none',
      overflow: isCompact ? 'hidden' : 'visible',
      whiteSpace: isCompact ? 'nowrap' : 'normal',
      wordWrap: isCompact ? 'normal' : 'break-word',
      padding: isDense ? '4px 12px' : isCompact ? '6px 12px' : '12px 8px',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      lineHeight: isDense ? 1.25 : 1.5,
      '& > *': {
        maxWidth: '100%',
        minWidth: 0,
      },
    },
    '& .MuiDataGrid-row': {
      ...dataGridSx['& .MuiDataGrid-row'],
      overflow: isCompact ? 'hidden' : 'visible',
    },
    '& .MuiDataGrid-main': {
      ...dataGridSx['& .MuiDataGrid-main'],
      overflow: useFixedViewportHeight ? 'auto' : 'visible',
    },
    '& .MuiDataGrid-cellContent': {
      width: '100%',
      maxWidth: '100%',
      overflow: isCompact ? 'hidden' : 'visible',
      textOverflow: isCompact ? 'ellipsis' : 'clip',
      whiteSpace: isCompact ? 'nowrap' : 'normal',
      wordWrap: isCompact ? 'normal' : 'break-word',
    },
  };

  const dataGrid = (
    <DataGrid
      rows={data}
      columns={updatedColumns}
      autoHeight={useAutoHeight}
      rowCount={pagination ? totalCount : undefined}
      paginationMode={pagination ? "server" : undefined}
      sortingMode={sortingMode}
      sortModel={sortModel}
      pageSizeOptions={pagination ? [Environment.OFFSET_REGISTERS ? Number(Environment.OFFSET_REGISTERS) : 10] : undefined}
      paginationModel={pagination ? { pageSize: Environment.OFFSET_REGISTERS ? Number(Environment.OFFSET_REGISTERS) : 10, page: paginationPage } : undefined}
      rowHeight={rowHeight}
      columnHeaderHeight={columnHeaderHeight}
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      onRowSelectionModelChange={handleRowSelection}
      isRowSelectable={isRowSelectable}
      onRowClick={handleRowClick}
      onPaginationModelChange={pagination ? (model) => handlePageChange(model.page) : undefined}
      onSortModelChange={handleSortModelChange}
      hideFooter={!pagination}
      disableMultipleRowSelection
      sx={gridSx}
      {...rest}
    />
  );

  if (useFixedViewportHeight) {
    return (
      <Box
        sx={{
          width: '100%',
          height: maxHeight,
          maxHeight,
          minHeight: 'unset',
          bgcolor: 'background.paper',
          borderRadius: 1,
          ...containerSx,
        }}
      >
        {dataGrid}
      </Box>
    );
  }

  if (useAutoHeight && maxHeight) {
    return (
      <Box
        sx={{
          width: '100%',
          maxHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          bgcolor: 'background.paper',
          borderRadius: 1,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 3,
          },
          ...containerSx,
        }}
      >
        {dataGrid}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: pagination ? 300 : undefined,
        bgcolor: 'background.paper',
        borderRadius: 1,
        ...containerSx,
      }}
    >
      {dataGrid}
    </Box>
  );
};

export { Table };
